// "Remember me on this device" for the student sign-in page (owner go-ahead
// 2026-10-03). Opt-in only: nothing is saved unless the family ticks the box.
//
// What is saved: the child's name and their login code, so next time the
// child taps their name and goes straight in. Only a student's own 8-character
// login code is ever saved -- a one-off 10-character activity code is not.
//
// The trade-off the owner accepted: anyone using this device can open that
// child's practice. That is why the box is off by default, and why every
// saved name has a "Forget" button.
//
// Browser storage can be missing or throw (private windows, blocked site
// data), so every read and write is wrapped and fails quietly: the page then
// simply behaves as if nothing was remembered.

export type RememberedLearner = { name: string; code: string };

const STORAGE_KEY = 'dodoskills.learn.remembered';
// A family device, not a classroom one: keep the list short.
export const MAX_REMEMBERED = 6;
const PERSONAL_CODE = /^[0-9A-HJKMNP-TV-Z]{8}$/;

function isRemembered(value: unknown): value is RememberedLearner {
  if (typeof value !== 'object' || value === null) return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.name === 'string' &&
    row.name.trim().length > 0 &&
    typeof row.code === 'string' &&
    PERSONAL_CODE.test(row.code)
  );
}

export function readRemembered(): RememberedLearner[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRemembered).slice(0, MAX_REMEMBERED);
  } catch {
    return [];
  }
}

function write(list: RememberedLearner[]): RememberedLearner[] {
  try {
    if (list.length === 0) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage unavailable: nothing is remembered, nothing breaks.
  }
  return list;
}

/**
 * Saves (or refreshes) a child. A new code for the same name replaces the
 * old one, so a code the teacher re-made doesn't leave a dead button behind.
 */
export function rememberLearner(
  name: string,
  code: string,
): RememberedLearner[] {
  const trimmed = name.trim();
  if (!trimmed || !PERSONAL_CODE.test(code)) return readRemembered();
  const rest = readRemembered().filter(
    (row) => row.code !== code && row.name !== trimmed,
  );
  return write([{ name: trimmed, code }, ...rest].slice(0, MAX_REMEMBERED));
}

export function forgetLearner(code: string): RememberedLearner[] {
  return write(readRemembered().filter((row) => row.code !== code));
}
